<template>
    <Teleport to="body">
        <div v-if="showModal" class="modal-backdrop" @mousedown="onBackdropMouseDown" @click="closeBackdropModal">
            <div v-show="showModal" id="modal-backdrop" class="modal" :style="{ 'max-width': width }" @mousedown.stop>
                <div class="modal-content">
                    <div class="header" :style="{ 'justify-content': headerType, 'font-size': titleSize }">
                        <h1 class="modal-title">
                            <slot name="title">Default Title</slot>
                        </h1>
                        <slot name="title-data"></slot>
                        <span class="close" v-if="showCloseBtn" @click="closeModal" title="Close">&times;</span>
                    </div>
                    <div class="modal-body" :style="{ height: height }">
                        <slot name="content">Default Content</slot>
                    </div>
                    <div class="modal-actions">
                        <slot name="actions"></slot>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { defineProps, defineEmits, watch, ref, onMounted, onUnmounted } from 'vue';

const props = defineProps({
    showModal: {
        type: Boolean,
        required: true,
    },
    backDrop: {
        type: Boolean,
        default: true,
    },
    width: {
        type: String,
        default: '800px',
    },
    height: {
        type: String,
        default: 'auto',
    },
    showCloseBtn: {
        type: Boolean,
        default: true,
    },
    headerType: {
        type: String,
        default: 'space-between',
    },
    titleSize: {
        type: String,
        default: '24px',
    },
});

const emit = defineEmits(['close-modal']);
const focusedElement = ref(null);
const originalBodyStyle = ref(null);
const mouseDownOnBackdrop = ref(false);

function closeModal() {
    emit('close-modal');
}

function onBackdropMouseDown() {
    mouseDownOnBackdrop.value = true;
}

function closeBackdropModal() {
    if (props.backDrop && mouseDownOnBackdrop.value && !focusedElement.value) {
        emit('close-modal');
    }
    mouseDownOnBackdrop.value = false;
}

function focusChanged(event) {
    if (event.target.tagName === 'INPUT') {
        focusedElement.value = event.target.tagName;
    }
}

function onFocusout() {
    setTimeout(() => {
        focusedElement.value = null;
    }, 300);
}

function lockBodyScroll() {
    const body = document.body;
    const html = document.documentElement;
    
    originalBodyStyle.value = {
        bodyOverflow: body.style.overflow,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyWidth: body.style.width,
        htmlOverflow: html.style.overflow,
        scrollY: window.scrollY
    };
    
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${originalBodyStyle.value.scrollY}px`;
    body.style.width = '100%';
    body.style.left = '0';
    
    html.style.overflow = 'hidden';
}

function unlockBodyScroll() {
    if (!originalBodyStyle.value) return;
    
    const body = document.body;
    const html = document.documentElement;
    const { bodyOverflow, bodyPosition, bodyTop, bodyWidth, htmlOverflow, scrollY } = originalBodyStyle.value;
    
    // Restore styles
    body.style.overflow = bodyOverflow || '';
    body.style.position = bodyPosition || '';
    body.style.top = bodyTop || '';
    body.style.width = bodyWidth || '';
    body.style.left = '';
    html.style.overflow = htmlOverflow || '';
    
    window.scrollTo(0, scrollY || 0);
}

watch(
    () => props.showModal,
    (newValue, oldValue) => {
        if (newValue) {
            lockBodyScroll();
            document.body.classList.add('modal-open');
            
            setTimeout(() => {
                const modal = document.getElementById('modal-backdrop');
                if (modal) {
                    modal.addEventListener('focusin', focusChanged);
                    modal.addEventListener('focusout', onFocusout);
                }
            }, 100);
        } else {
            unlockBodyScroll();
            document.body.classList.remove('modal-open');
            
            const modal = document.getElementById('modal-backdrop');
            if (modal) {
                modal.removeEventListener('focusin', focusChanged);
                modal.removeEventListener('focusout', onFocusout);
            }
        }
    },
    { immediate: true }
);

onUnmounted(() => {
    if (props.showModal) {
        unlockBodyScroll();
    }
});
</script>

<style scoped lang="scss">
.modal-backdrop {
    position: fixed !important;
    inset: 0;
    width: 100vw;
    height: 100vh;
    backdrop-filter: blur(4px) grayscale(1) brightness(0.5);
    -webkit-backdrop-filter: blur(4px) grayscale(1) brightness(0.5);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    z-index: 1001;
    overflow-y: auto;
    padding: max(5vh, 40px) 20px;
    box-sizing: border-box;
    transform: none !important;
    margin: 0 !important;
}

.modal {
    background-color: var(--blanc);
    width: 80%;
    max-width: 1200px;
    max-height: calc(100vh - max(10vh, 80px));
    min-height: 80vh;
    border-radius: 8px;
    position: relative;
    padding: 20px;
    line-height: 1.5;
    filter: grayscale(0);
    transition: all 0.3s ease-in-out;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    flex-shrink: 0;
}

.modal-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    h2 {
        margin: 0;
    }
}

.modal-title {
    width: 96%;
    font-size: 1.5em;
    margin-bottom: 1em;
}

.modal-body {
    font-size: 1em;
    line-height: 1.5;
    flex: 1;
    overflow-y: auto;
    overflow-x: visible;
    min-height: 0;
}

.close {
    position: absolute;
    top: 35px;
    right: 16px;
    color: var(--noir);
    font-size: 33px;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    width: 30px;
    height: 30px;
    text-align: center;
    line-height: 30px;
    padding: 0;
    transition: background-color 0.3s;
    &:hover {
        background-color: var(--gris-50);
    }
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 1rem;
}

.modal_button {
    margin: 0 2rem;
    border: 2px solid var(--gris-800);
    border-radius: 7px;
    padding: 0.76rem 1rem;
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;
    background-color: var(--gris-200);
    float: left;
}

.modal_button:hover {
    background-color: var(--gris-200);
}
</style>